import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { COLORS } from '../lib/styleGuide';

// Style constants
const FONTS = {
  heading: { fontFamily: 'Inter, system-ui, sans-serif' },
  body: { fontFamily: 'Inter, system-ui, sans-serif' }
};

export default function AdminArchivedProducts() {
  const navigate = useNavigate();
  const { currentUser, hasRole } = useAuth();
  const [archivedProducts, setArchivedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSeller, setSelectedSeller] = useState('all');

  // Check if user is admin
  useEffect(() => {
    if (currentUser && !hasRole('admin')) {
      navigate('/');
      return;
    }
    
    if (currentUser && hasRole('admin')) {
      fetchArchivedProducts();
    }
  }, [currentUser, hasRole, navigate]);

  const fetchArchivedProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const productsRef = collection(db, 'products');
      const q = query(productsRef, where('archived', '==', true));
      const snapshot = await getDocs(q);
      
      const products = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setArchivedProducts(products);
    } catch (err) {
      console.error('Error fetching archived products:', err);
      setError(err.message || 'Failed to fetch archived products');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  // Filter products based on search and seller
  const filteredProducts = archivedProducts.filter(product => {
    const matchesSearch = !searchTerm || 
      product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.sellerId?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesSeller = selectedSeller === 'all' || product.sellerId === selectedSeller;
    
    return matchesSearch && matchesSeller;
  });

  // Get unique sellers for filter
  const sellers = [...new Set(archivedProducts.map(p => p.sellerId))].filter(Boolean);

  if (!currentUser || !hasRole('admin')) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: '#F9F9F9', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        paddingBottom: '120px',
        fontFamily: 'Montserrat, sans-serif'
      }}>
        <div style={{ textAlign: 'center' }}>
          <h1 style={{ fontSize: 32, color: '#222', marginBottom: 16 }}>Access Denied</h1>
          <p style={{ color: '#757575', fontSize: 18 }}>You don't have permission to view this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: '#F9F9F9',
      paddingBottom: '120px',
      fontFamily: 'Montserrat, sans-serif'
    }}>
      {/* Header */}
      <div style={{
        background: COLORS.NEUTRAL_WHITE,
        borderBottom: '1px solid #E0E0E0',
        padding: '20px 16px'
      }}>
        <div style={{
          maxWidth: 1200,
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div>
            <h1 style={{
              fontSize: 24,
              fontWeight: 700,
              color: COLORS.NEUTRAL_TEXT,
              margin: '0 0 4px 0',
              ...FONTS.heading
            }}>
              Archived Products
            </h1>
            <p style={{
              fontSize: 14,
              color: COLORS.ACCENT_GRAY,
              margin: 0,
              ...FONTS.body
            }}>
              View and manage archived products for compliance and audits
            </p>
          </div>
          
          <button
            onClick={() => navigate('/admin-dashboard')}
            style={{
              background: COLORS.PRIMARY_BROWN,
              color: COLORS.NEUTRAL_WHITE,
              border: 'none',
              borderRadius: 8,
              padding: '8px 16px',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              ...FONTS.body
            }}
          >
            Back to Admin
          </button>
        </div>
      </div>

      {/* Content */}
      <div style={{
        maxWidth: 1200,
        margin: '0 auto',
        padding: '24px 16px'
      }}>
        {/* Filters */}
        <div style={{
          background: COLORS.NEUTRAL_WHITE,
          borderRadius: 12,
          padding: '20px',
          marginBottom: '24px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <div style={{
            display: 'flex',
            gap: '16px',
            flexWrap: 'wrap',
            alignItems: 'center'
          }}>
            {/* Search */}
            <div style={{ flex: 1, minWidth: 200 }}>
              <label style={{
                fontSize: 12,
                fontWeight: 600,
                color: COLORS.NEUTRAL_TEXT,
                marginBottom: '4px',
                display: 'block',
                ...FONTS.body
              }}>
                Search Products
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by product name or seller ID..."
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #E0E0E0',
                  borderRadius: 6,
                  fontSize: 14,
                  ...FONTS.body
                }}
              />
            </div>

            {/* Seller Filter */}
            <div style={{ minWidth: 200 }}>
              <label style={{
                fontSize: 12,
                fontWeight: 600,
                color: COLORS.NEUTRAL_TEXT,
                marginBottom: '4px',
                display: 'block',
                ...FONTS.body
              }}>
                Filter by Seller
              </label>
              <select
                value={selectedSeller}
                onChange={(e) => setSelectedSeller(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #E0E0E0',
                  borderRadius: 6,
                  fontSize: 14,
                  background: COLORS.NEUTRAL_WHITE,
                  ...FONTS.body
                }}
              >
                <option value="all">All Sellers</option>
                {sellers.map(sellerId => (
                  <option key={sellerId} value={sellerId}>
                    {sellerId}
                  </option>
                ))}
              </select>
            </div>

            {/* Stats */}
            <div style={{ minWidth: 150 }}>
              <div style={{
                fontSize: 12,
                fontWeight: 600,
                color: COLORS.ACCENT_GRAY,
                marginBottom: '4px',
                ...FONTS.body
              }}>
                Total Archived
              </div>
              <div style={{
                fontSize: 24,
                fontWeight: 700,
                color: COLORS.PRIMARY_BROWN,
                ...FONTS.heading
              }}>
                {filteredProducts.length}
              </div>
            </div>
          </div>
        </div>

        {/* Products List */}
        {loading ? (
          <div style={{
            background: COLORS.NEUTRAL_WHITE,
            borderRadius: 12,
            padding: '40px',
            textAlign: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <div style={{
              fontSize: 16,
              color: COLORS.ACCENT_GRAY,
              ...FONTS.body
            }}>
              Loading archived products...
            </div>
          </div>
        ) : error ? (
          <div style={{
            background: COLORS.NEUTRAL_WHITE,
            borderRadius: 12,
            padding: '40px',
            textAlign: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <div style={{
              fontSize: 16,
              color: COLORS.ERROR_RED,
              ...FONTS.body
            }}>
              Error: {error}
            </div>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div style={{
            background: COLORS.NEUTRAL_WHITE,
            borderRadius: 12,
            padding: '40px',
            textAlign: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <div style={{
              fontSize: 16,
              color: COLORS.ACCENT_GRAY,
              ...FONTS.body
            }}>
              No archived products found
            </div>
          </div>
        ) : (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}>
            {filteredProducts.map(product => (
              <div
                key={product.id}
                style={{
                  background: COLORS.NEUTRAL_WHITE,
                  borderRadius: 12,
                  padding: '20px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  border: '1px solid #E0E0E0'
                }}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '16px'
                }}>
                  {/* Product Image */}
                  <img
                    src={product.imageUrl || 'https://via.placeholder.com/80'}
                    alt={product.name}
                    style={{
                      width: 80,
                      height: 80,
                      objectFit: 'cover',
                      borderRadius: 8,
                      border: '1px solid #E0E0E0'
                    }}
                  />
                  
                  {/* Product Info */}
                  <div style={{ flex: 1 }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '8px'
                    }}>
                      <h3 style={{
                        fontSize: 18,
                        fontWeight: 600,
                        color: COLORS.NEUTRAL_TEXT,
                        margin: '0 0 4px 0',
                        ...FONTS.heading
                      }}>
                        {product.name}
                      </h3>
                      <span style={{
                        background: COLORS.ERROR_RED,
                        color: COLORS.NEUTRAL_WHITE,
                        padding: '4px 8px',
                        borderRadius: 4,
                        fontSize: 11,
                        fontWeight: 600,
                        ...FONTS.body
                      }}>
                        ARCHIVED
                      </span>
                    </div>
                    
                    <p style={{
                      fontSize: 14,
                      color: COLORS.ACCENT_GRAY,
                      margin: '0 0 12px 0',
                      lineHeight: 1.4,
                      ...FONTS.body
                    }}>
                      {product.description || 'No description available'}
                    </p>
                    
                                         {/* Product Details Grid */}
                     <div style={{
                       display: 'grid',
                       gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                       gap: '12px',
                       fontSize: '14px',
                       ...FONTS.body
                     }}>
                       <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                         <span style={{ color: COLORS.ACCENT_GRAY }}>Price:</span>
                         <span style={{ fontWeight: 600, color: COLORS.PRIMARY_BROWN }}>
                           {formatCurrency(product.price)}
                         </span>
                       </div>
                       
                       <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                         <span style={{ color: COLORS.ACCENT_GRAY }}>Category:</span>
                         <span>{product.listingType || product.itemCategory || 'N/A'}</span>
                       </div>
                       
                       <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                         <span style={{ color: COLORS.ACCENT_GRAY }}>Seller:</span>
                         <span>{product.sellerId || 'N/A'}</span>
                       </div>
                       
                       <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                         <span style={{ color: COLORS.ACCENT_GRAY }}>Original ID:</span>
                         <span style={{ fontSize: '12px', fontFamily: 'monospace' }}>{product.original_product_id || 'N/A'}</span>
                       </div>
                       
                       <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                         <span style={{ color: COLORS.ACCENT_GRAY }}>Created:</span>
                         <span>{formatDate(product.createdAt)}</span>
                       </div>
                       
                       <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                         <span style={{ color: COLORS.ACCENT_GRAY }}>Archived:</span>
                         <span>{formatDate(product.archived_at)}</span>
                       </div>
                       
                       <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                         <span style={{ color: COLORS.ACCENT_GRAY }}>Reason:</span>
                         <span>{product.archived_reason || 'seller_deletion'}</span>
                       </div>
                     </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
